import { useEffect } from 'react';

const SITE = 'avdhootvishram.in';
const BASE = `https://${SITE}`;
const DEFAULT_IMAGE = `${BASE}/logo512.png`;

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const removeMeta = (attr, key) => {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
};

const applyJsonLd = (schema) => {
  document.head.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
  if (!schema) return;
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.setAttribute('data-seo-jsonld', 'true');
  s.textContent = JSON.stringify(schema);
  document.head.appendChild(s);
};

/**
 * Client-side SEO manager for the SPA. Sets <title>, description, keywords,
 * canonical, robots, Open Graph / Twitter tags and optional JSON-LD for the
 * current page. Dependency-free (no react-helmet) to keep the build stable on
 * React 19.
 */
const Seo = ({
  title,
  description,
  keywords,
  path = '/',
  image = DEFAULT_IMAGE,
  jsonLd,
  robots = 'index, follow',
}) => {
  useEffect(() => {
    const fullTitle = title ? `${title} | Shri Avdhoot Vishram` : 'Shri Avdhoot Vishram';
    const url = path && path !== '/' ? `${BASE}${path}` : `${BASE}/`;

    document.title = fullTitle;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', robots);
    upsertMeta('name', 'googlebot', robots);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Open Graph
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'Shri Avdhoot Vishram');
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:image:alt', title || 'Shri Avdhoot Vishram');
    upsertMeta('property', 'og:locale', 'en_US');

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    applyJsonLd(jsonLd);

    return () => {
      removeMeta('name', 'description');
      removeMeta('name', 'keywords');
      // Leave document.title and canonical as-is (last-write-wins within SPA).
    };
  }, [title, description, keywords, path, image, jsonLd, robots]);

  return null;
};

export default Seo;
